/**
 * Create Event Dialog
 *
 * Reason: Allows admins to create new events in their organization
 */

import { FC, useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Alert,
  CircularProgress,
  Box,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { useQueryClient } from 'react-query';
import { useCreateEvent, LIST_EVENTS_CACHE_KEY } from '@/adapters/events-v2';

interface CreateEventDialogProps {
  open: boolean;
  onClose(): void;
}

/**
 * Dialog for creating new events
 *
 * @reason Provides form UI for admins to create events
 */
const CreateEventDialog: FC<CreateEventDialogProps> = ({ open, onClose }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const createEventMutation = useCreateEvent();

  /**
   * Handles form submission to create new event
   *
   * @reason Creates event and refreshes the event list
   */
  const handleCreate = async (): Promise<void> => {
    setError(null);

    // Basic validation
    if (!name.trim()) {
      setError('Event name is required');
      return;
    }

    try {
      await createEventMutation.mutateAsync({
        name: name.trim(),
        description: description.trim() || undefined,
        isActive,
      });

      // Invalidate event list to refresh
      await queryClient.invalidateQueries(LIST_EVENTS_CACHE_KEY);

      // Close dialog and reset form
      handleClose();
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to create event';
      setError(errorMessage);
    }
  };

  /**
   * Handles dialog close and resets form
   *
   * @reason Cleans up form state when dialog is closed
   */
  const handleClose = (): void => {
    setName('');
    setDescription('');
    setIsActive(true);
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Event</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <TextField
          margin="dense"
          label="Event Name"
          type="text"
          fullWidth
          required
          value={name}
          onChange={(e) => setName(e.target.value)}
          disabled={createEventMutation.isLoading}
          sx={{ mb: 2, mt: 1 }}
        />

        <TextField
          margin="dense"
          label="Description"
          type="text"
          fullWidth
          multiline
          rows={3}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          disabled={createEventMutation.isLoading}
          sx={{ mb: 2 }}
        />

        <FormControlLabel
          control={
            <Switch
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              disabled={createEventMutation.isLoading}
            />
          }
          label="Active"
        />

        {createEventMutation.isLoading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <CircularProgress />
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={createEventMutation.isLoading}>
          Cancel
        </Button>
        <Button onClick={handleCreate} variant="contained" disabled={createEventMutation.isLoading}>
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateEventDialog;
